import type { StepProps } from "../types";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Field, Input, Select, Spinner } from "@/components/ui";

const EMPLOYMENT_TYPES = [
  { value: "full_time", label: "Full-time" },
  { value: "part_time", label: "Part-time" },
  { value: "casual",    label: "Casual" },
  { value: "director",  label: "Director" },
] as const;

export default function Step2Employment({ context, draft, updateField }: StepProps) {
  const emp = context.employee;

  const { data: roles, isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roles")
        .select("role_name, display_name")
        .eq("is_employee_role", true)
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
      <Field label="Start date *">
        <Input
          type="date"
          value={draft.start_date ?? emp?.start_date ?? ""}
          onChange={e => updateField("start_date", e.target.value)}
        />
      </Field>

      <Field label="Employment type *">
        <Select
          value={draft.employment_type ?? emp?.employment_type ?? ""}
          onChange={e => updateField("employment_type", e.target.value)}
        >
          <option value="">—</option>
          {EMPLOYMENT_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </Select>
      </Field>

      <div className="md:col-span-2">
        <Field label="Primary role *">
          {rolesLoading ? (
            <div className="flex items-center gap-2 text-text-muted text-sm py-2.5">
              <Spinner size={14} /> loading roles…
            </div>
          ) : (
            <Select
              value={draft.primary_role_name ?? emp?.primary_role_name ?? ""}
              onChange={e => updateField("primary_role_name", e.target.value)}
            >
              <option value="">—</option>
              {(roles ?? []).map(r => (
                <option key={r.role_name} value={r.role_name}>
                  {r.display_name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    </div>
  );
}
